import React, {
  createContext,
  useContext,
  useState,
  type ReactNode,
} from "react";

import type { AxiosResponse } from "axios";
import axios from "axios";

import type { CommonBlock } from "~/src/types/__generated__/cc-api/api";

interface CommonBlocksState {
  data: CommonBlock[];
  loading: boolean;
  error: string | null;
  count: number;
  query: string;
}

interface CommonBlocksContextType {
  blocks: CommonBlocksState;
  loadBlocks: (queryParameters?: string) => void;
  addBlock: (block: CommonBlock) => void;
  updateBlock: (uuid: string, patch: Partial<CommonBlock>) => void;
  removeBlock: (uuid: string) => void;
}

const CommonBlocksContext = createContext<CommonBlocksContextType | undefined>(
  undefined,
);

export { CommonBlocksContext };

interface CommonBlocksProviderProps {
  children: ReactNode;
}

export const CommonBlocksProvider = ({
  children,
}: CommonBlocksProviderProps) => {
  const [blocks, setBlocks] = useState<CommonBlocksState>({
    data: [],
    loading: true,
    error: null,
    count: 0,
    query: "",
  });

  const loadBlocks = (queryParameters?: string) => {
    setBlocks((prev) => {
      return { ...prev, loading: true, error: null };
    });

    axios
      .get(
        `population/common-blocks${queryParameters ? `${queryParameters}` : ""}`,
      )
      .then(
        (
          res: AxiosResponse<{
            result: { results: CommonBlock[]; count: number };
          }>,
        ) => {
          setBlocks({
            data: res.data.result.results ?? [],
            loading: false,
            error: null,
            count: res.data.result.count ?? 0,
            query: queryParameters ?? "",
          });
        },
      )
      .catch(() => {
        setBlocks({
          data: [],
          loading: false,
          error: "Failed to load blocks",
          count: 0,
          query: queryParameters ?? "",
        });
      });
  };

  const addBlock = (block: CommonBlock) => {
    setBlocks((prev) => {
      return {
        ...prev,
        data: [block, ...prev.data],
        count: prev.count + 1,
      };
    });
  };

  const updateBlock = (uuid: string, patch: Partial<CommonBlock>) => {
    setBlocks((prev) => {
      return {
        ...prev,
        data: prev.data.map((b) => (b.uuid === uuid ? { ...b, ...patch } : b)),
      };
    });
  };

  const removeBlock = (uuid: string) => {
    setBlocks((prev) => {
      return {
        ...prev,
        data: prev.data.filter((b) => b.uuid !== uuid),
        count: Math.max(0, prev.count - 1),
      };
    });
  };

  return (
    <CommonBlocksContext.Provider
      value={{
        blocks,
        loadBlocks,
        addBlock,
        updateBlock,
        removeBlock,
      }}
    >
      {children}
    </CommonBlocksContext.Provider>
  );
};

export const useCommonBlocksContext = (): CommonBlocksContextType => {
  const context = useContext(CommonBlocksContext);
  if (!context) {
    throw new Error(
      "useCommonBlocksContext must be used within a CommonBlocksProvider",
    );
  }
  return context;
};
